import { Component, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { ChartjsComponent } from '@coreui/angular-chartjs';
import { ChartData, ChartOptions } from 'chart.js';
import { UserMatch } from '../../../../../models/users/user-match.model';
import { MatchState } from '../../../../../enums/users/match-state.enum';
import { UserMatchesResultsResponse } from '../../../../../models/users/responses/api-users-matches-results-response.model';
import { UserMatchesFiltersRequest } from '../../../../../models/users/requests/api-users-get-user-matchs-request.model';
import { ApiUsersService } from '../../../../../services/users/api/api-users.service';
import { AlertsService } from '../../../../../services/alerts.service';
import { DateService } from '../../../../../services/date.service';
import { UserMatchDetailModalComponent } from './modals/user-match-detail-modal/user-match-detail-modal.component';

@Component({
  selector: 'app-user-matchs-tab',
  templateUrl: './user-matchs-tab.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, CollapseModule, ChartjsComponent, UserMatchDetailModalComponent]
})
export class UserMatchsTabComponent {

  @ViewChild('matchDetailModal') matchDetailModal!: UserMatchDetailModalComponent;

  @Input() userId: number = 0;

  protected readonly MatchState = MatchState;

  isLoading: boolean = false;
  isFilterOpen: boolean = false;
  isStatsOpen: boolean = false;
  requestFilters: UserMatchesFiltersRequest = new UserMatchesFiltersRequest();

  matches: UserMatch[] = [];
  resultsPerPage: number = 25;
  pageNumber: number = 1;
  totalResults: number = 0;
  exitedCount: number = 0;

  chartData: ChartData = { labels: [], datasets: [] };
  chartOptions: ChartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
  };

  get totalPages(): number {
    return Math.ceil(this.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;

    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);

    const sorted = Array.from(visibleSet).sort((a, b) => a - b);

    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }

    return result;
  }

  goToPage(page: number): void {
    this.pageNumber = page;
    this.refreshMatchesList();
  }

  constructor(public dateService: DateService, private apiUsersService: ApiUsersService, private alertsService: AlertsService) {}

  load(): void {
    this.pageNumber = 1;
    this.refreshMatchesList();
  }

  changeFilterOpenState(): void {
    this.isFilterOpen = !this.isFilterOpen;
  }

  changeStatsOpenState(): void {
    this.isStatsOpen = !this.isStatsOpen;
  }

  onClickFilter(): void {
    this.pageNumber = 1;
    this.refreshMatchesList();
  }

  onClickResetFilters(): void {
    this.requestFilters = new UserMatchesFiltersRequest();
    this.pageNumber = 1;
    this.refreshMatchesList();
  }

  onClickPaginationPrevious(): void {
    this.pageNumber--;
    this.refreshMatchesList();
  }

  onClickPaginationNext(): void {
    this.pageNumber++;
    this.refreshMatchesList();
  }

  private refreshMatchesList(): void {
    this.isLoading = true;

    this.apiUsersService.getUserMatchs(this.userId, this.pageNumber, this.resultsPerPage, this.requestFilters).then(
      (response: UserMatchesResultsResponse) => {
        this.isLoading = false;
        this.matches = response.results;
        this.totalResults = response.resultsTotal;
        this.exitedCount = response.exitedCount;
        this.chartData = {
          labels: response.matchsTablesStatistics.map(s => `Table ${s.matchTableId ?? '?'} | V${s.variantId ?? '?'} | ${s.matchTableCost}`),
          datasets: [{
            data: response.matchsTablesStatistics.map(s => s.count),
            backgroundColor: 'rgba(50, 110, 200, 0.7)',
            borderColor: 'rgba(50, 110, 200, 1)',
            borderWidth: 1
          }]
        };
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading matches (' + error + ')');
      }
    );
  }
}
